/**
 * Hook para gerenciar tickets
 */

import { useQuery, useMutation, useQueryClient, useInfiniteQuery } from '@tanstack/react-query';
import type { ActivityAttachment, ActivityResponse, TicketTimelineResponse, TransferRequest } from '@troia-v3/types';
import { ticketsService } from '../services';
import type {
  TicketQuery,
  TicketListResponse,
  TicketResponse,
  CreateTicketRequest,
  UpdateTicketRequest
} from '../types';

/**
 * Hook para buscar lista de tickets
 */
export function useTickets(query?: TicketQuery, options?: { enabled?: boolean }) {
  return useQuery<TicketListResponse>({
    queryKey: ['tickets', query],
    queryFn: () => ticketsService.getTickets(query),
    staleTime: 30 * 1000, // 30 seconds
    enabled: options?.enabled ?? true,
  });
}

/**
 * Quem pode ser escolhido como responsável de ticket no pipeline (cadastro e
 * transferência): participantes do pipeline em qualquer papel; pipeline sem
 * participantes → todos os usuários ativos. Sem pipeline, a query não roda.
 */
export function useTicketAssigneesDropdown(pipelineId: string | null | undefined, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['tickets-assignees-dropdown', pipelineId],
    queryFn: () => ticketsService.getAssigneesForPipeline(pipelineId!),
    staleTime: 5 * 60 * 1000,
    enabled: (options?.enabled ?? true) && !!pipelineId,
  });
}

/**
 * Operadores do filtro "por operador" do pipeline: quem participa dele
 * (qualquer papel) + quem tem ticket atribuído nele, mesmo desativado ou
 * excluído. Sem pipeline, a query não roda.
 */
export function useTicketOperatorsDropdown({
  pipelineId,
  scope,
  enabled = true,
}: {
  pipelineId: string | null | undefined;
  scope?: 'team' | 'all';
  enabled?: boolean;
}) {
  return useQuery({
    queryKey: ['tickets-operators-dropdown', pipelineId, scope ?? 'all'],
    queryFn: () => ticketsService.getOperatorsForPipeline(pipelineId!, scope),
    staleTime: 60 * 1000,
    enabled: enabled && !!pipelineId,
  });
}

/**
 * Hook para buscar ticket por ID
 */
export function useTicketById(id?: string) {
  return useQuery<TicketResponse>({
    queryKey: ['ticket', id],
    queryFn: () => ticketsService.getTicketById(id!),
    enabled: !!id,
    staleTime: 30 * 1000,
  });
}

/**
 * Hook para criar ticket
 */
export function useCreateTicket() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateTicketRequest) => ticketsService.createTicket(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tickets'] });
      // Board windowed (SP3) tem query key própria — invalidar junto pra
      // mutações refletirem no kanban (espelho do CRM leads/leads-kanban).
      queryClient.invalidateQueries({ queryKey: ['tickets-kanban'] });
    },
  });
}

/**
 * Hook para atualizar ticket
 * Aceita um objeto com id e os campos a serem atualizados
 *
 * Invalida também `['ticket-timeline', id]` — updates geram activities
 * (mudança de stage/prioridade/tags/etc.) que a Timeline V2 precisa refletir.
 * Partial matching do React Query cobre as variantes `{includeInternal, limit}`.
 */
export function useUpdateTicket() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: { id: string } & Partial<UpdateTicketRequest>) => {
      const { id, ...data } = params;
      return ticketsService.updateTicket(id, data as UpdateTicketRequest);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['tickets'] });
      queryClient.invalidateQueries({ queryKey: ['tickets-kanban'] });
      queryClient.invalidateQueries({ queryKey: ['ticket', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['ticket-timeline', variables.id] });
    },
  });
}

/**
 * Hook para deletar ticket
 */
export function useDeleteTicket() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => ticketsService.deleteTicket(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tickets'] });
      queryClient.invalidateQueries({ queryKey: ['tickets-kanban'] });
    },
  });
}

/**
 * Hook para transferir ticket para outro usuário/equipe/pipeline (ou desatribuir)
 *
 * Endpoint padronizado: `POST /tickets/:id/transfer`
 * Body aceita XOR `toUserId | toTeamId` (null desatribui) + `toPipelineId?`/
 * `toStageId?` (Task 4/10 do Plano A) + `reason?`/`notes?`.
 *
 * Resolve com `{ ticket, warnings? }` — `TransferTicketDialog` usa `warnings`
 * pra exibir aviso pós-transferência (ex.: responsável desatribuído por
 * inelegibilidade no pipeline de destino).
 */
export function useTransferTicket() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ ticketId, body }: { ticketId: string; body: TransferRequest }) =>
      ticketsService.transfer(ticketId, body),
    onSuccess: (result, variables) => {
      queryClient.invalidateQueries({ queryKey: ['tickets'] });
      queryClient.invalidateQueries({ queryKey: ['tickets-kanban'] });
      queryClient.invalidateQueries({ queryKey: ['ticket', variables.ticketId] });
      // Transferência grava activity de assignment — Timeline V2 precisa refletir
      queryClient.invalidateQueries({ queryKey: ['ticket-timeline', variables.ticketId] });
      if (result?.ticket?.id) {
        queryClient.setQueryData(['ticket', result.ticket.id], result.ticket);
      }
    },
  });
}

/**
 * Hook para resolver ticket (Task 5/9 — Plano A: reforma do Resolver).
 *
 * `resolutionSummary` é OPCIONAL — o backend move o ticket pra stage
 * `category: 'resolved'` do pipeline mesmo sem resumo. Erro `NO_RESOLVED_STAGE`
 * (422, quando o pipeline não tem essa stage) é tratado localmente pelo
 * `ResolveTicketModal` — não invalida nada em caso de erro.
 */
export function useResolveTicket() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: { resolutionSummary?: string; internalNotes?: string; customerSatisfaction?: number };
    }) => ticketsService.resolveTicket(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['tickets'] });
      queryClient.invalidateQueries({ queryKey: ['tickets-kanban'] });
      queryClient.invalidateQueries({ queryKey: ['ticket', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['ticket-timeline', variables.id] });
    },
  });
}

/**
 * Hook para fechar ticket
 */
export function useCloseTicket() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => ticketsService.closeTicket(id),
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: ['tickets'] });
      queryClient.invalidateQueries({ queryKey: ['tickets-kanban'] });
      queryClient.invalidateQueries({ queryKey: ['ticket', id] });
      queryClient.invalidateQueries({ queryKey: ['ticket-timeline', id] });
    },
  });
}

/**
 * Hook para reabrir ticket
 */
export function useReopenTicket() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => ticketsService.reopenTicket(id),
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: ['tickets'] });
      queryClient.invalidateQueries({ queryKey: ['tickets-kanban'] });
      queryClient.invalidateQueries({ queryKey: ['ticket', id] });
      queryClient.invalidateQueries({ queryKey: ['ticket-timeline', id] });
    },
  });
}

/**
 * Hook para mover ticket entre stages (drag & drop no Kanban)
 */
export function useMoveTicketStage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, stageId }: { id: string; stageId: string }) =>
      ticketsService.moveTicketToStage(id, stageId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['tickets'] });
      queryClient.invalidateQueries({ queryKey: ['tickets-kanban'] });
      queryClient.invalidateQueries({ queryKey: ['ticket', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['ticket-timeline', variables.id] });
    },
  });
}

/**
 * Hook para a Timeline V2 do ticket (`GET /tickets/:id/timeline`, Spec A
 * Task 8) — infinite query por cursor opaco. `getNextPageParam` usa
 * `nextCursor` só quando `hasMore` (backend já garante essa invariante, mas
 * checamos os dois defensivamente).
 *
 * Query key: `['ticket-timeline', ticketId, { includeInternal, limit }]` —
 * as opções ENTRAM na key (fix pós-review M1): variantes com/sem notas
 * internas não compartilham cache e alternar `includeInternal` em runtime
 * refetcha sozinho. `includeInternal` é normalizado pro default `true`
 * (mesmo default do backend) pra key ser estável entre "omitido" e
 * "explícito true". Invalidations por prefixo (`['ticket-timeline']` ou
 * `['ticket-timeline', ticketId]`) seguem matcheando todas as variantes via
 * partial matching do React Query — nenhum caller invalida por igualdade
 * exata hoje (verificado por grep).
 */
export function useTicketTimeline(
  ticketId?: string,
  options?: { includeInternal?: boolean; limit?: number; enabled?: boolean },
) {
  const includeInternal = options?.includeInternal ?? true;
  const limit = options?.limit;

  return useInfiniteQuery({
    queryKey: ['ticket-timeline', ticketId, { includeInternal, limit }],
    queryFn: ({ pageParam }: { pageParam: string | undefined }) =>
      ticketsService.getTimeline(ticketId!, {
        cursor: pageParam,
        limit,
        includeInternal,
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage: TicketTimelineResponse) =>
      lastPage.hasMore ? lastPage.nextCursor : undefined,
    enabled: (options?.enabled ?? true) && !!ticketId,
    staleTime: 30 * 1000,
  });
}

/**
 * Hook para enviar resposta pública ao ticket (composer — aba "Resposta")
 * via `POST /tickets/:id/reply`. Invalida o próprio ticket (defensivo —
 * timestamps/campos derivados podem mudar com a resposta) e a Timeline V2
 * (`['ticket-timeline', ticketId]` — partial matching do React Query cobre
 * as variantes `{includeInternal, limit}` da query key, ver `useTicketTimeline`)
 * pra `TicketTimelineV2` refletir a nova mensagem sem precisar de refresh manual.
 */
export function useReplyTicket() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ ticketId, body }: { ticketId: string; body: { content: string; attachments?: ActivityAttachment[] } }) =>
      ticketsService.replyTicket(ticketId, body),
    onSuccess: (_activity: ActivityResponse, variables) => {
      queryClient.invalidateQueries({ queryKey: ['ticket', variables.ticketId] });
      queryClient.invalidateQueries({ queryKey: ['ticket-timeline', variables.ticketId] });
    },
  });
}

/**
 * Hook para adicionar nota interna ao ticket (composer — aba "Nota Interna")
 * via `POST /tickets/:id/activities` com `isInternal: true` sempre fixo —
 * a distinção resposta pública/nota interna acontece na escolha do
 * hook (`useReplyTicket` vs `useAddTicketNote`), não em um parâmetro booleano.
 */
export function useAddTicketNote() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      ticketId,
      body,
    }: {
      ticketId: string;
      body: { content: string; ticketTitle?: string; attachments?: ActivityAttachment[] };
    }) => ticketsService.addTicketActivity(ticketId, { ...body, isInternal: true }),
    onSuccess: (_activity: ActivityResponse, variables) => {
      queryClient.invalidateQueries({ queryKey: ['ticket', variables.ticketId] });
      queryClient.invalidateQueries({ queryKey: ['ticket-timeline', variables.ticketId] });
    },
  });
}
